// src/pages/Metodologia.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Metodologia.css";

const LANGUAGE_STORAGE_KEY = "zedro_academy_language";
const LANGUAGE_CHANGE_EVENT = "zedro:languagechange";

const copy = {
  es: {
    heroTitle: "Una metodología para convertir principios en práctica diaria.",
    heroSubtitle:
      "ZEDRO Academy no está diseñado para que acumules más información. Está diseñado para ayudarte a comprender, ordenar, practicar y sostener una forma de vida con raíz, claridad y propósito.",
    viewMembership: "Ver membresía",
    seeHowItWorks: "Ver cómo funciona",
    showcaseAria: "Vista previa del ecosistema ZEDRO",
    altMain: "Vista principal de un sistema mensual dentro de ZEDRO Academy",
    altDay: "Vista de un día de formación dentro de ZEDRO Academy",
    altJournal: "Vista de la Bitácora ZEDRO",
    altMission: "Recorrido de micro-misión diaria dentro de ZEDRO Academy",
    altConexion: "Vista de Conexión ZEDRO y Cuarto de Guerra",

    problemLabel: "El punto de quiebre",
    problemTitle: "No falta información. Falta dirección sostenida.",
    problemLead:
      "Vivimos rodeados de contenido, cursos, consejos y herramientas. Aun así, muchas personas siguen sintiendo dispersión, desgaste y poca continuidad.",
    problem1Title: "Saber no siempre transforma.",
    problem1Text:
      "Puedes conocer principios correctos y aun así no saber cómo llevarlos a tus decisiones, hábitos y vida diaria.",
    problem2Title: "La motivación no sostiene el proceso.",
    problem2Text:
      "Cuando no hay estructura, el avance termina dependiendo de la emoción, la presión o la fuerza del momento.",
    problem3Title: "ZEDRO Academy trabaja la raíz.",
    problem3Text:
      "Por eso la metodología convierte principios eternos en sistemas concretos que ordenan pensamiento, decisiones, hábitos y acción cotidiana.",

    methodLabel: "El método ZEDRO Academy",
    methodTitle: "Un ciclo simple para formar una vida más sólida.",
    methodLead:
      "Cada sistema en ZEDRO Academy está diseñado para llevarte por un recorrido práctico: comprender lo que necesitas ver, ordenar lo que necesita alinearse, practicar con acciones concretas y sostener el fruto en la vida cotidiana.",
    flowAria: "Ciclo de formación ZEDRO",
    step1Title: "Comprender",
    step1Text:
      "Recibes dirección clara para mirar un área de tu vida desde principios, no desde ruido, comparación o presión.",
    step2Title: "Ordenar",
    step2Text:
      "Alineas pensamientos, prioridades y decisiones para dejar de avanzar de manera dispersa.",
    step3Title: "Practicar",
    step3Text:
      "Bajas la formación a acciones reales mediante micro-misiones, reflexión, aplicación diaria y bitácora.",
    step4Title: "Sostener",
    step4Text:
      "No se trata de emoción momentánea, sino de formar criterio, carácter y fruto visible con el tiempo.",
    methodStatementSmall: "No es consumo pasivo.",
    methodStatementStrong:
      "Es formación aplicada con estructura, práctica y continuidad.",

    insideLabel: "Así se vive dentro",
    insideTitle:
      "Un ecosistema diseñado para formar vida, no solo para entregar contenido.",
    insideLead:
      "Dentro de ZEDRO Academy, cada persona avanza por sistemas mensuales, días guiados, micro-misiones, bitácora personal y espacios de conexión para integrar lo aprendido en su vida real.",
    systemKicker: "01 · Sistemas",
    systemTitle: "Sistemas mensuales con ruta clara.",
    systemText:
      "Cada sistema trabaja un área concreta de nuestro diseño original durante 28 días, con progreso, continuidad y una estructura que ayuda a avanzar sin improvisar.",
    dayKicker: "02 · Día ZEDRO Academy",
    dayTitle: "Formación diaria paso a paso.",
    dayText:
      "Cada día guía a la persona por bloques de verdad, alineamiento interno, acción, aplicación y reflexión.",
    missionKicker: "03 · Micro-misión",
    missionTitle: "Acciones pequeñas que dejan evidencia.",
    missionText:
      "La micro-misión convierte lo aprendido en una acción concreta y registra avance en el Altar de Testimonio.",
    journalKicker: "04 · Bitácora",
    journalTitle: "Todo el proceso en un solo lugar.",
    journalText:
      "La Bitácora reúne reflexiones, respuestas, decisiones, evidencias, micro-misiones y registros personales.",
    connectionKicker: "05 · Conexión ZEDRO",
    connectionTitle: "Un espacio para oración, dirección y memoria.",
    connectionText:
      "Conexión ZEDRO integra el Cuarto de Guerra: plan de batalla, peticiones, oraciones y victorias para permanecer firme y recordar el proceso.",

    differenceLabel: "Qué hace diferente a ZEDRO Academy",
    differenceTitle:
      "No es una plataforma para consumir contenido. Es una formación para construir vida.",
    differenceLead:
      "ZEDRO Academy no busca llenar tu mente de más información, sino ayudarte a convertir principios eternos en práctica diaria, decisiones concretas y evidencia real de transformación.",
    diff1Title: "No es curso",
    diff1Text:
      "No se trata de ver lecciones y avanzar por consumo. Se trata de trabajar una ruta formativa con aplicación real.",
    diff2Title: "No es motivación",
    diff2Text:
      "No depende de emoción momentánea. Usa estructura, repetición y práctica para sostener el proceso.",
    diff3Title: "No es contenido suelto",
    diff3Text:
      "Cada sistema, día, micro-misión y registro está conectado con una ruta de formación intencional.",
    diff4Title: "Es formación aplicada",
    diff4Text:
      "Sistema, práctica, bitácora y continuidad para ordenar la vida desde la raíz y construir con propósito.",

    audienceLabel: "Para quién es",
    audienceTitle: "ZEDRO Academy es para quienes desean construir con dirección.",
    yesTitle: "Sí es para ti si...",
    yesItems: [
      "Deseas vivir con más claridad, orden y propósito.",
      "Quieres ordenar tu interior antes de construir afuera.",
      "Valoras la práctica diaria, la responsabilidad y la constancia.",
    ],
    noTitle: "No es para ti si...",
    noItems: [
      "Buscas fórmulas rápidas sin proceso.",
      "Quieres consumir contenido sin aplicar nada.",
      "Prefieres delegar la responsabilidad de construir una vida coherente.",
    ],

    ctaLabel: "Da el siguiente paso",
    ctaQuote: "Una vida bien construida no es casualidad.",
    ctaText:
      "Comienza a formarte con sistemas prácticos diseñados para ordenar tu interior, fortalecer tus decisiones y construir con propósito.",
  },

  fr: {
    heroTitle:
      "Une méthode pour vivre les principes au quotidien.",
    heroSubtitle:
      "ZEDRO Academy n’est pas conçu pour accumuler davantage d’information. Il est conçu pour vous aider à comprendre, ordonner, pratiquer et soutenir une manière de vivre avec racine, clarté et intention.",
    viewMembership: "Voir l’abonnement",
    seeHowItWorks: "Découvrir",
    showcaseAria: "Aperçu de l’écosystème ZEDRO",
    altMain: "Vue principale d’un système mensuel dans ZEDRO Academy",
    altDay: "Vue d’une journée de formation dans ZEDRO Academy",
    altJournal: "Vue de la Bitácora ZEDRO",
    altMission: "Parcours d’une micro-mission quotidienne dans ZEDRO Academy",
    altConexion: "Vue de Conexión ZEDRO et du Cuarto de Guerra",

    problemLabel: "Le point de rupture",
    problemTitle: "Ce n’est pas l’information qui manque. C’est une direction durable.",
    problemLead:
      "Nous vivons entourés de contenus, de cours, de conseils et d’outils. Pourtant, beaucoup de personnes ressentent encore de la dispersion, de l’usure et peu de continuité.",
    problem1Title: "Savoir ne transforme pas toujours.",
    problem1Text:
      "Vous pouvez connaître des principes justes et ne pas savoir comment les appliquer à vos décisions, vos habitudes et votre vie quotidienne.",
    problem2Title: "La motivation ne soutient pas le processus.",
    problem2Text:
      "Sans structure, l’avancement finit par dépendre de l’émotion, de la pression ou de la force du moment.",
    problem3Title: "ZEDRO Academy travaille la racine.",
    problem3Text:
      "C’est pourquoi la méthodologie transforme des principes durables en systèmes concrets qui ordonnent la pensée, les décisions, les habitudes et l’action quotidienne.",

    methodLabel: "La méthode ZEDRO Academy",
    methodTitle: "Un cycle simple pour former une vie plus solide.",
    methodLead:
      "Chaque système dans ZEDRO Academy est conçu pour vous guider dans un parcours pratique : comprendre ce qu’il faut voir, ordonner ce qui doit être aligné, pratiquer par des actions concrètes et soutenir le fruit dans la vie quotidienne.",
    flowAria: "Cycle de formation ZEDRO",
    step1Title: "Comprendre",
    step1Text:
      "Vous recevez une direction claire pour regarder un domaine de votre vie à partir de principes, et non à partir du bruit, de la comparaison ou de la pression.",
    step2Title: "Ordonner",
    step2Text:
      "Vous alignez pensées, priorités et décisions afin de ne plus avancer de manière dispersée.",
    step3Title: "Pratiquer",
    step3Text:
      "Vous transformez la formation en actions réelles grâce aux micro-missions, à la réflexion, à l’application quotidienne et à la bitácora.",
    step4Title: "Soutenir",
    step4Text:
      "Il ne s’agit pas d’une émotion passagère, mais de former du discernement, du caractère et un fruit visible dans le temps.",
    methodStatementSmall: "Ce n’est pas une consommation passive.",
    methodStatementStrong:
      "C’est une formation appliquée avec structure, pratique et continuité.",

    insideLabel: "Comment cela se vit à l’intérieur",
    insideTitle:
      "Un écosystème conçu pour former la vie, pas seulement pour livrer du contenu.",
    insideLead:
      "Dans ZEDRO Academy, chaque personne avance à travers des systèmes mensuels, des journées guidées, des micro-missions, une bitácora personnelle et des espaces de connexion pour intégrer ce qui est appris dans la vie réelle.",
    systemKicker: "01 · Systèmes",
    systemTitle: "Des systèmes mensuels avec un parcours clair.",
    systemText:
      "Chaque système travaille un domaine concret de notre design original pendant 28 jours, avec progression, continuité et une structure qui aide à avancer sans improviser.",
    dayKicker: "02 · Journée ZEDRO Academy",
    dayTitle: "Une formation quotidienne pas à pas.",
    dayText:
      "Chaque journée guide la personne à travers des blocs de vérité, d’alignement intérieur, d’action, d’application et de réflexion.",
    missionKicker: "03 · Micro-mission",
    missionTitle: "De petites actions qui laissent une trace.",
    missionText:
      "La micro-mission transforme ce qui a été appris en une action concrète et enregistre l’avancement dans l’Altar de Testimonio.",
    journalKicker: "04 · Bitácora",
    journalTitle: "Tout le processus en un seul endroit.",
    journalText:
      "La Bitácora rassemble réflexions, réponses, décisions, évidences, micro-missions et registres personnels.",
    connectionKicker: "05 · Conexión ZEDRO",
    connectionTitle: "Un espace pour la prière, la direction et la mémoire.",
    connectionText:
      "Conexión ZEDRO intègre le Cuarto de Guerra : plan de bataille, demandes, prières et victoires pour demeurer ferme et se souvenir du processus.",

    differenceLabel: "Ce qui rend ZEDRO Academy différent",
    differenceTitle:
      "Ce n’est pas une plateforme pour consommer du contenu. C’est une formation pour construire la vie.",
    differenceLead:
      "ZEDRO Academy ne cherche pas à remplir votre esprit de plus d’information, mais à vous aider à transformer des principes durables en pratique quotidienne, en décisions concrètes et en évidences réelles de transformation.",
    diff1Title: "Ce n’est pas un cours",
    diff1Text:
      "Il ne s’agit pas de regarder des leçons et d’avancer par consommation. Il s’agit de travailler un parcours formatif avec une application réelle.",
    diff2Title: "Ce n’est pas de la motivation",
    diff2Text:
      "Cela ne dépend pas d’une émotion passagère. La méthode utilise structure, répétition et pratique pour soutenir le processus.",
    diff3Title: "Ce n’est pas du contenu isolé",
    diff3Text:
      "Chaque système, journée, micro-mission et registre est relié à un parcours de formation intentionnel.",
    diff4Title: "C’est une formation appliquée",
    diff4Text:
      "Système, pratique, bitácora et continuité pour ordonner la vie depuis la racine et construire avec intention.",

    audienceLabel: "Pour qui",
    audienceTitle: "ZEDRO Academy est pour ceux qui désirent construire avec direction.",
    yesTitle: "C’est pour vous si...",
    yesItems: [
      "Vous désirez vivre avec plus de clarté, d’ordre et de sens.",
      "Vous voulez ordonner votre monde intérieur avant de construire à l’extérieur.",
      "Vous valorisez la pratique quotidienne, la responsabilité et la constance.",
    ],
    noTitle: "Ce n’est pas pour vous si...",
    noItems: [
      "Vous cherchez des formules rapides sans processus.",
      "Vous voulez consommer du contenu sans rien appliquer.",
      "Vous préférez déléguer la responsabilité de construire une vie cohérente.",
    ],

    ctaLabel: "Passez à l’étape suivante",
    ctaQuote: "Une vie bien construite n’est pas le fruit du hasard.",
    ctaText:
      "Commencez à vous former avec des systèmes pratiques conçus pour ordonner votre monde intérieur, fortifier vos décisions et construire avec intention.",
  },
};

function getInitialLanguage() {
  if (typeof window === "undefined") return "es";
  const savedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  return savedLanguage === "fr" ? "fr" : "es";
}

export default function Metodologia() {
  const [language, setLanguage] = useState(getInitialLanguage);
  const t = copy[language] || copy.es;

  useEffect(() => {
    const syncLanguage = () => setLanguage(getInitialLanguage());

    window.addEventListener(LANGUAGE_CHANGE_EVENT, syncLanguage);
    window.addEventListener("storage", syncLanguage);

    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, syncLanguage);
      window.removeEventListener("storage", syncLanguage);
    };
  }, []);

  return (
    <main className="meth-page">
      <section className="meth-wrap" aria-labelledby="meth-title">
        <header className="meth-hero meth-hero--premium">
          <div className="meth-heroText">
            <h1 id="meth-title" className="meth-title">
              {t.heroTitle}
            </h1>

            <p className="meth-subtitle">{t.heroSubtitle}</p>

            <div className="meth-heroActions">
              <Link className="meth-btn meth-btnPrimary" to="/store">
                {t.viewMembership}
              </Link>

              <a className="meth-btn meth-btnGhost" href="#como-se-vive">
                {t.seeHowItWorks}
              </a>
            </div>
          </div>

          <div className="meth-heroShowcase" aria-label={t.showcaseAria}>
            <img
              className="meth-heroShot meth-heroShot--main"
              src="/assets/images/metodologia/metodologia-sistemas-vista-principal.png"
              alt={t.altMain}
            />

            <img
              className="meth-heroShot meth-heroShot--floating meth-heroShot--one"
              src="/assets/images/metodologia/metodologia-dia-momentos-verdad.png"
              alt={t.altDay}
            />

            <img
              className="meth-heroShot meth-heroShot--floating meth-heroShot--two"
              src="/assets/images/metodologia/metodologia-bitacora-recorrido.png"
              alt={t.altJournal}
            />
          </div>
        </header>

        <section className="meth-card">
          <section className="meth-section meth-section--problem meth-problemBlock">
            <div className="meth-problemIntro">
              <span className="meth-sectionLabel">{t.problemLabel}</span>

              <h2 className="meth-h2">{t.problemTitle}</h2>

              <p className="meth-lead">{t.problemLead}</p>
            </div>

            <div className="meth-problemGrid">
              <div className="meth-problemCard">
                <span>01</span>
                <h3>{t.problem1Title}</h3>
                <p>{t.problem1Text}</p>
              </div>

              <div className="meth-problemCard">
                <span>02</span>
                <h3>{t.problem2Title}</h3>
                <p>{t.problem2Text}</p>
              </div>

              <div className="meth-problemCard meth-problemCard--highlight">
                <span>03</span>
                <h3>{t.problem3Title}</h3>
                <p>{t.problem3Text}</p>
              </div>
            </div>
          </section>

          <section className="meth-section meth-methodBlock">
            <div className="meth-methodHeader">
              <span className="meth-sectionLabel">{t.methodLabel}</span>

              <h2 className="meth-h2">{t.methodTitle}</h2>

              <p className="meth-lead">{t.methodLead}</p>
            </div>

            <div className="meth-methodFlow" aria-label={t.flowAria}>
              <article className="meth-methodStep">
                <div className="meth-methodNum">01</div>
                <div className="meth-methodLine" />
                <h3>{t.step1Title}</h3>
                <p>{t.step1Text}</p>
              </article>

              <article className="meth-methodStep">
                <div className="meth-methodNum">02</div>
                <div className="meth-methodLine" />
                <h3>{t.step2Title}</h3>
                <p>{t.step2Text}</p>
              </article>

              <article className="meth-methodStep">
                <div className="meth-methodNum">03</div>
                <div className="meth-methodLine" />
                <h3>{t.step3Title}</h3>
                <p>{t.step3Text}</p>
              </article>

              <article className="meth-methodStep">
                <div className="meth-methodNum">04</div>
                <h3>{t.step4Title}</h3>
                <p>{t.step4Text}</p>
              </article>
            </div>

            <div className="meth-methodStatement">
              <span>{t.methodStatementSmall}</span>
              <strong>{t.methodStatementStrong}</strong>
            </div>
          </section>

          <section id="como-se-vive" className="meth-section meth-insideBlock">
            <div className="meth-insideHeader">
              <span className="meth-sectionLabel">{t.insideLabel}</span>

              <h2 className="meth-h2">{t.insideTitle}</h2>

              <p className="meth-lead">{t.insideLead}</p>
            </div>

            <div className="meth-showcaseList">
              <article className="meth-showcaseItem meth-showcaseItem--wide">
                <div className="meth-showcaseText">
                  <span className="meth-showcaseKicker">{t.systemKicker}</span>
                  <h3>{t.systemTitle}</h3>
                  <p>{t.systemText}</p>
                </div>

                <img
                  src="/assets/images/metodologia/metodologia-sistemas-vista-principal.png"
                  alt={t.altMain}
                />
              </article>

              <article className="meth-showcaseItem">
                <div className="meth-showcaseText">
                  <span className="meth-showcaseKicker">{t.dayKicker}</span>
                  <h3>{t.dayTitle}</h3>
                  <p>{t.dayText}</p>
                </div>

                <img
                  src="/assets/images/metodologia/metodologia-dia-momentos-verdad.png"
                  alt={t.altDay}
                />
              </article>

              <article className="meth-showcaseItem">
                <div className="meth-showcaseText">
                  <span className="meth-showcaseKicker">{t.missionKicker}</span>
                  <h3>{t.missionTitle}</h3>
                  <p>{t.missionText}</p>
                </div>

                <img
                  src="/assets/images/metodologia/metodologia-micro-mision-recorrido.png"
                  alt={t.altMission}
                />
              </article>

              <article className="meth-showcaseItem meth-showcaseItem--center">
                <div className="meth-showcaseText">
                  <span className="meth-showcaseKicker">{t.journalKicker}</span>
                  <h3>{t.journalTitle}</h3>
                  <p>{t.journalText}</p>
                </div>

                <img
                  src="/assets/images/metodologia/metodologia-bitacora-recorrido.png"
                  alt={t.altJournal}
                />
              </article>

              <article className="meth-showcaseItem meth-showcaseItem--wide">
                <div className="meth-showcaseText">
                  <span className="meth-showcaseKicker">
                    {t.connectionKicker}
                  </span>
                  <h3>{t.connectionTitle}</h3>
                  <p>{t.connectionText}</p>
                </div>

                <img
                  src="/assets/images/metodologia/metodologia-conexion-zedro.png"
                  alt={t.altConexion}
                />
              </article>
            </div>
          </section>

          <section className="meth-section meth-differenceBlock">
            <div className="meth-differenceHeader">
              <span className="meth-sectionLabel">{t.differenceLabel}</span>

              <h2 className="meth-h2">{t.differenceTitle}</h2>

              <p className="meth-lead">{t.differenceLead}</p>
            </div>

            <div className="meth-differenceGrid">
              <article className="meth-differenceCard">
                <span>{t.diff1Title}</span>
                <p>{t.diff1Text}</p>
              </article>

              <article className="meth-differenceCard">
                <span>{t.diff2Title}</span>
                <p>{t.diff2Text}</p>
              </article>

              <article className="meth-differenceCard">
                <span>{t.diff3Title}</span>
                <p>{t.diff3Text}</p>
              </article>

              <article className="meth-differenceCard meth-differenceCard--strong">
                <span>{t.diff4Title}</span>
                <p>{t.diff4Text}</p>
              </article>
            </div>
          </section>

          <section className="meth-section meth-audienceBlock">
            <div className="meth-audienceHeader">
              <span className="meth-sectionLabel">{t.audienceLabel}</span>

              <h2 className="meth-h2">{t.audienceTitle}</h2>
            </div>

            <div className="meth-cols">
              <div className="meth-col meth-col--yes">
                <h3>{t.yesTitle}</h3>
                <ul className="meth-list">
                  {t.yesItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className="meth-col meth-col--no">
                <h3>{t.noTitle}</h3>
                <ul className="meth-list">
                  {t.noItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="meth-cta meth-ctaFinal">
            <span className="meth-sectionLabel">{t.ctaLabel}</span>

            <p className="meth-quote">{t.ctaQuote}</p>

            <p className="meth-ctaText">{t.ctaText}</p>

            <div className="meth-actions">
              <Link className="meth-btn meth-btnPrimary" to="/store">
                {t.viewMembership}
              </Link>
            </div>
          </section>
        </section>
      </section>
    </main>
  );
}
